import { type ComponentType } from 'react';
import Image from '@atlaskit/image';
import { Text } from '@atlaskit/primitives/compiled';
import { FormattedMessage, useIntl } from 'react-intl';
import { type JSX } from 'react/jsx-runtime';

import { commonTourMessages, type marketplaceOpsTourMessages } from './messages';
import spotlightMediaImage from './assets/spotlight-media.svg';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';

interface StepComponentProps {
	dismiss: () => void;
	next: () => void;
	done: () => void;
	stepIndex: number;
	totalSteps: number;
	experimentMessages: typeof marketplaceOpsTourMessages;
}

interface TourStep {
	Component: ComponentType<StepComponentProps>;
}

const createTour = (experimentMessages: typeof marketplaceOpsTourMessages): TourStep[] => [
	{
		// Step 1
		Component: ({ dismiss, next, stepIndex, totalSteps }: StepComponentProps) => {
			const { formatMessage } = useIntl();

			return (
				<PopoverProvider>
					<PopoverTarget>
						<div />
					</PopoverTarget>
					<PopoverContent dismiss={dismiss} placement="right-start" isVisible={true}>
						<SpotlightCard>
							<SpotlightHeader>
								<SpotlightHeadline>
									<FormattedMessage {...commonTourMessages.step1Heading} />
								</SpotlightHeadline>
								<SpotlightControls>
									<SpotlightDismissControl autoFocus={false} />
								</SpotlightControls>
							</SpotlightHeader>
							<SpotlightMedia>
								<Image
									src={spotlightMediaImage}
									alt={formatMessage(commonTourMessages.step1Heading)}
								/>
							</SpotlightMedia>
							<SpotlightBody>
								<Text>
									<FormattedMessage
										{...experimentMessages.step1Description}
										values={{
											b: (chunks: React.ReactNode) => <b>{chunks}</b>,
										}}
									/>
								</Text>
							</SpotlightBody>
							<SpotlightFooter>
								<SpotlightStepCount>
									<FormattedMessage
										{...commonTourMessages.stepCount}
										values={{
											currentStep: stepIndex + 1,
											totalSteps,
										}}
									/>
								</SpotlightStepCount>
								<SpotlightActions>
									<SpotlightPrimaryAction onClick={next}>
										<FormattedMessage {...commonTourMessages.nextButton} />
									</SpotlightPrimaryAction>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					</PopoverContent>
				</PopoverProvider>
			);
		},
	},
	{
		// Step 2
		Component: ({ dismiss, done, stepIndex, totalSteps }: StepComponentProps) => {
			return (
				<PopoverProvider>
					<PopoverTarget>
						<div />
					</PopoverTarget>
					<PopoverContent dismiss={dismiss} placement="right-start" isVisible={true}>
						<SpotlightCard>
							<SpotlightHeader>
								<SpotlightHeadline>
									<FormattedMessage {...commonTourMessages.step2Heading} />
								</SpotlightHeadline>
								<SpotlightControls>
									<SpotlightDismissControl autoFocus={false} />
								</SpotlightControls>
							</SpotlightHeader>
							<SpotlightBody>
								<Text>
									<FormattedMessage
										{...commonTourMessages.step2Description}
										values={{
											b: (chunks: React.ReactNode) => <b>{chunks}</b>,
										}}
									/>
								</Text>
							</SpotlightBody>
							<SpotlightFooter>
								<SpotlightStepCount>
									<FormattedMessage
										{...commonTourMessages.stepCount}
										values={{
											currentStep: stepIndex + 1,
											totalSteps,
										}}
									/>
								</SpotlightStepCount>
								<SpotlightActions>
									<SpotlightPrimaryAction onClick={done}>
										<FormattedMessage {...commonTourMessages.primaryAction} />
									</SpotlightPrimaryAction>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					</PopoverContent>
				</PopoverProvider>
			);
		},
	},
];

export interface OperationsDiscoverySpotlightTourProps {
	dismiss: () => void;
	next: () => void;
	done: () => void;
	stepIndex: number;
	totalSteps: number;
	experimentMessages: typeof marketplaceOpsTourMessages;
}

export const OperationsDiscoverySpotlightTour = ({
	dismiss,
	next,
	done,
	stepIndex,
	totalSteps,
	experimentMessages,
}: OperationsDiscoverySpotlightTourProps): JSX.Element => {
	const tour = createTour(experimentMessages);
	const step = tour[stepIndex];
	const Step = step?.Component || (() => <></>);
	return (
		<Step
			dismiss={dismiss}
			next={next}
			done={done}
			stepIndex={stepIndex}
			totalSteps={totalSteps}
			experimentMessages={experimentMessages}
		/>
	);
};
